import { Box, Card, CardActionArea, CardActions, Chip, LinearProgress, Stack, Typography } from '@mui/material'
import FileDownloadButton from 'components/Files/FileDownloadButton'
import { getIncomeEntryPhotoDownloadUrl, getIncomeEntryRemitoDownloadUrl } from 'constants/urls'
import { typographyStyles } from 'constants/styles'
import { INCOME_ENTRY_STATUS } from 'constants/index'
import { STATUS_COLOR } from 'helpers/incomeEntry'
import type { IncomeEntrySummary } from 'types'
import {
  MdChevronRight,
  MdInventory2,
  MdLocalShipping,
  MdCheckCircle,
  MdWarningAmber,
  MdPerson,
} from 'react-icons/md'
import { formatDateOnlyDMY } from 'helpers'

interface IncomeCardProps {
  income: IncomeEntrySummary
  onClick: (id: string, status: string) => void
}

export default function IncomeEntrySummaryCard({income, onClick }: IncomeCardProps) {
  const {
    client,
    numero_remito,
    fecha_ingreso,
    status,
    result,
    associated_client,
    operator,
  } = income;

  const articlesQuantity = income.articles.length;
  const bundlesQuantity = income.articles.reduce((acc, article) => acc + article.bundles.length, 0);
  const resolvedBundlesQuantity = income.articles.reduce((acc, article) => acc + article.bundles.filter(bundle => bundle.arrived).length, 0);

  const color = STATUS_COLOR[status]

  return (
    <Card
      variant="outlined"
      sx={{
        borderLeft: 4,
        borderLeftColor: `${color}.main`,
        flexShrink: 0,
      }}
    >
      <CardActionArea onClick={() => onClick(income.id.toString(), status)} sx={{ p: 1.75 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* remito + fecha */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography sx={{ ...typographyStyles.monospace, fontWeight: 700 }} noWrap>
                {numero_remito}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 1 }}>
                {formatDateOnlyDMY(fecha_ingreso)}
              </Typography>
            </Stack>

            {/* client */}
            <Typography variant="body2" sx={{ mt: 0.5 }} noWrap>
              {client.razon_social}
            </Typography>

            {/* transport */}
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
              <MdLocalShipping size={14} style={{ opacity: 0.6, flexShrink: 0 }} />
              <Typography variant="caption" color="text.secondary" noWrap>
                {associated_client?.razon_social ?? 'Sin cliente asociado'}
              </Typography>
            </Stack>

            {/* operator */}
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
              <MdPerson size={14} style={{ opacity: 0.6, flexShrink: 0 }} />
              <Typography variant="caption" color="text.secondary" noWrap>
                {operator?.first_name && operator?.last_name ? `${operator?.first_name} ${operator?.last_name}` : 'Sin operador asignado'}
              </Typography>
            </Stack>

            {/* quantities */}
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.75 }}>
              <MdInventory2 size={14} style={{ opacity: 0.6, flexShrink: 0 }} />
              <Typography variant="caption" color="text.secondary">
                {articlesQuantity} producto{articlesQuantity !== 1 ? 's' : ''} · {bundlesQuantity} fardo
                {bundlesQuantity !== 1 ? 's' : ''}
              </Typography>
            </Stack>

            {/* status indicator */}
            {status === INCOME_ENTRY_STATUS.IN_PROGRESS && (
              <Box sx={{ mt: 1 }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="caption" color="warning.main" fontWeight={700}>
                    En progreso
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {resolvedBundlesQuantity}/{bundlesQuantity} fardos
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={bundlesQuantity === 0 ? 0 : (resolvedBundlesQuantity / bundlesQuantity) * 100}
                  color="warning"
                  sx={{ height: 5, borderRadius: 3 }}
                />
              </Box>
            )}

            {status === INCOME_ENTRY_STATUS.VERIFIED && (
              <Box sx={{ mt: 1 }}>
                {result === 'with_observations' ? (
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    icon={<MdWarningAmber />}
                    label="Verificado con observaciones"
                  />
                ) : (
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    icon={<MdCheckCircle />}
                    label="Verificado"
                  />
                )}
              </Box>
            )}
          </Box>

          <MdChevronRight size={22} style={{ flexShrink: 0, opacity: 0.5, marginTop: 2 }} />
        </Stack>
      </CardActionArea>
      <CardActions sx={{ px: 1.75, pb: 1.25, justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }} disableSpacing>
        <FileDownloadButton
          requestUrl={getIncomeEntryRemitoDownloadUrl(income.id)}
          label="Descargar remito"
          disabled={!income.pdf_file_id}
          disabledReason="Remito no disponible"
        />
        {Boolean(income.photo_file_id) && (
          <FileDownloadButton
            requestUrl={getIncomeEntryPhotoDownloadUrl(income.id)}
            label="Descargar foto"
          />
        )}
      </CardActions>
    </Card>
  )
}
